import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import moment from "moment";
import investIMG from '../images/login.png';
import Verification from '../components/Verification';
import {
    plans,
    fetchUserData,
    handleStartTrade,
    handleClaimWithdrawal
} from '../lib/tradeLogic';

const Trade = ({ user, accountBalances, defaultAmount = "" }) => {
    // State variables
    const [selectedPlan, setSelectedPlan] = useState("");
    const [investmentAmount, setInvestmentAmount] = useState(defaultAmount);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [userRole, setUserRole] = useState("");
    const [userGroup, setUserGroup] = useState("");
    const [userReport, setUserReport] = useState("");
    const [isTradeInProgress, setIsTradeInProgress] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [investmentTransactions, setInvestmentTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showVerificationBox, setShowVerificationBox] = useState(false);

    const navigate = useNavigate();

    // Fetch user role, group, report, and any active investment transactions
    useEffect(() => {
        const loadUser = async () => {
            const data = await fetchUserData(user);
            if (data) {
                setUserRole(data.role);
                setInvestmentTransactions(data.activeTransactions);
                setUserGroup(data.userData.group || "");
                setUserReport(data.userData.report || "");
            }
            setIsLoading(false);
        };
        loadUser();
    }, [user]);

    // Determine if current user is an investor or admin
    const isInvestor = userRole === "investor";
    const isAdmin = userRole === "admin";

    // Handle trade initiation
    const onStartTrade = async () => {
        setError("");
        setSuccess("");
        setIsTradeInProgress(true);

        // Prevent admin accounts from starting trades
        if (isAdmin) {
            setError("Admins cannot trade for security reasons.");
            setIsTradeInProgress(false);
            return;
        }


        // Attempt to start trade
        try {
            await handleStartTrade({
                user,
                selectedPlan,
                investmentAmount,
                accountBalances,
                setSuccess,
                setError,
                navigate
            });
        } catch (err) {
            console.error("Start trade error:", err);
            setError("Failed to start trade. Please try again.");
        } finally {
            setIsTradeInProgress(false);
        }
    };

    
    // Handle profit claim logic
    const onClaimWithdrawal = async () => {
        setError("");
        setSuccess("");
        setIsClaiming(true);

        try {
            const transaction = investmentTransactions[0];
            if (!transaction) {
                setError("No trade transaction found to claim.");
                setIsClaiming(false);
                return;
            }

            await handleClaimWithdrawal({
                user,
                transaction,
                accountBalances,
                setSuccess,
                setError,
                navigate
            });
        } catch (err) {
            console.error("Claim error:", err);
            setError("Failed to claim profit. Please try again.");
        } finally {
            setIsClaiming(false);
        }
    };

    return (
        <div className="" id="transfer">
            {/* Show loading spinner while user data loads */}
            {isLoading ? (
                <div className="c">
                    <div className="center">
                        <ClipLoader color="#D4292A" size={100} />
                    </div>
                    <div className="center user-names">Please wait, loading your account details...</div>
                </div>
            ) : isAdmin ? (
                // Admins are not allowed to trade
                <>
                    <div className="center">
                        <img src={investIMG} alt="Investing"  className="box-image"/>
                    </div>
                    <h4 className="center user-names">
                        Admins cannot trade for security reasons.
                    </h4>
                </>
            ) : isInvestor ? (
                // Investor view when trade is in progress
                <>
                    <h3 className="user-names">Your trade is currently in progress.</h3>
                    <div className="transaction-history">
                        {investmentTransactions.map((transaction, index) => {
                            let closestDate = null;
                            let closestAmount = null;

                            // Determine next eligible profit payment
                            transaction.profitSchedule?.forEach((schedule) => {
                                const scheduled = moment(schedule.date);
                                const now = moment();
                                if (scheduled.isSameOrAfter(now, 'day')) {
                                    if (!closestDate || scheduled.isBefore(closestDate)) {
                                        closestDate = scheduled;
                                        closestAmount = schedule.amount;
                                    }
                                }
                            });

                            return (
                                <div key={index}>
                                    <li className="user-names"><strong className="user-names">Plan:</strong> {transaction.planDetails?.name}</li>

                                    {/* Show next payment details */}
                                    {closestDate && (
                                        <div className="profit-schedule">
                                            <li><strong className="user-names">Next Payment Date:</strong> {closestDate.format("MMMM D, YYYY")}</li>
                                            <li><strong className="user-names">Scheduled Profit:</strong> ${closestAmount}</li>
                                            <li><strong className="user-names">Status:</strong> {closestDate.isSame(moment(), 'day') ? 'Available for claim' : 'Upcoming'}</li>

                                            {/* Show claim or verify button depending on user group status */}
                                            {closestDate.isSame(moment(), 'day') && (
                                                userGroup === "deactivated" ? (
                                                    <>
                                                        <p style={{ color: "red", marginTop: "1rem" }}>{userReport}</p>
                                                        <button
                                                            className="verify-transactions-button"
                                                            onClick={() => setShowVerificationBox(true)}
                                                        >
                                                            Verify Transactions
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        onClick={onClaimWithdrawal}
                                                        className="delete-button"
                                                        disabled={isClaiming}
                                                    >
                                                        {isClaiming ? "Claiming Trade..." : "Claim Trade"}
                                                    </button>
                                                )
                                            )}

                                            {/* Verification popup component */}
                                            {showVerificationBox && (
                                                <Verification setShowVerificationBox={setShowVerificationBox} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                // View to start a new trade
                <>
                    <h2>Start a Trade</h2>

                    {/* Plan selection */}
                    <div>
                        <label htmlFor="plan" className="transaction-head">
                            Select Plan:
                        </label>
                        <select
                            id="plan"
                            value={selectedPlan}
                            className="input-field"
                            onChange={(e) => setSelectedPlan(e.target.value)}
                        >
                            <option value="">-- Select a Plan --</option>
                            {plans.map((plan) => (
                                <option key={plan.name} value={plan.name}>
                                    {plan.name} - Min: ${plan.amount}, Duration: {plan.duration} days
                                </option>
                            ))}
                        </select>
                        <p className="inForm">
                            <Link to="/investments" className="banner-button">
                                Explore Investments
                            </Link>
                        </p>
                    </div>

                    {/* Amount entry */}
                    <div>
                        <label htmlFor="amount" className="transaction-head">
                            Investment Amount:
                        </label>
                        <input
                            id="amount"
                            type="number"
                            className="input-field"
                            value={investmentAmount}
                            onChange={(e) => setInvestmentAmount(e.target.value)}
                            placeholder="Enter investment amount"
                        />
                        <p className="inForm">
                            Available Balance: ${((accountBalances.Investments || 0) + (accountBalances.Trading || 0)).toFixed(2)}
                        </p>
                    </div>

                    {/* Error/success messages */}
                    {error && <p style={{ color: "red" }}>{error}</p>}
                    {success && <p style={{ color: "green" }}>{success}</p>}

                    {/* Trade button */}
                    <div className="center btn-cover">
                        <button
                            onClick={onStartTrade}
                            disabled={
                                !selectedPlan ||
                                !investmentAmount ||
                                isTradeInProgress ||
                                parseFloat(investmentAmount) < plans.find((p) => p.name === selectedPlan)?.amount ||
                                parseFloat(investmentAmount) > (accountBalances.Investments || 0)
                            }
                            className="delete-button"
                        >
                            {isTradeInProgress ? "Starting Trade..." : "Start Trade"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Trade;
